export enum LessonPlanStatus {
  /** Initial state when lesson plan is first created */
  DRAFT = 'draft',
  /** User has completed the Basics step (grade, subject selection) */
  BASICS_COMPLETED = 'basics_completed',
  /** User has completed the Settings step (duration, instructional model, tech) */
  SETTINGS_COMPLETED = 'settings_completed',
  /** AI has generated learning objectives based on the topic and grade level */
  OBJECTIVES_GENERATED = 'objectives_generated',
  /** Lesson structure/blocks have been generated with phases, activities, and timing */
  STRUCTURE_GENERATED = 'structure_generated',
  /** Slides and detailed content have been generated for each lesson block */
  CONTENT_GENERATED = 'content_generated',
  /** Lesson plan is finalized and ready for use */
  COMPLETED = 'completed',
}

/**
 * Generation status for async AI generation processes.
 * Used for status polling during long-running generation tasks.
 */
export enum GenerationStatus {
  /** No generation in progress */
  IDLE = 'idle',
  /** Currently generating learning objectives */
  GENERATING_OBJECTIVES = 'generating_objectives',
  /** Currently generating lesson structure */
  GENERATING_STRUCTURE = 'generating_structure',
  /** Currently generating slides */
  GENERATING_SLIDES = 'generating_slides',
  /** Generation failed */
  FAILED = 'failed',
}

export interface GenerationStatusResponse {
  id: string;
  status: LessonPlanStatus;
  generationStatus: GenerationStatus;
  generationError: string | null;
}

export interface StartGenerationResponse {
  lessonPlanId: string;
  generationStatus: GenerationStatus;
  message: string;
}

/**
 * Maps lesson plan status to the maximum step the user can access
 * Steps: 1=Basics, 2=Settings, 3=Topic, 4=Objectives, 5=Structure, 6=Present
 */
export const STATUS_TO_MAX_STEP: Record<LessonPlanStatus, number> = {
  [LessonPlanStatus.DRAFT]: 1,
  [LessonPlanStatus.BASICS_COMPLETED]: 2,
  [LessonPlanStatus.SETTINGS_COMPLETED]: 3,
  [LessonPlanStatus.OBJECTIVES_GENERATED]: 4,
  [LessonPlanStatus.STRUCTURE_GENERATED]: 5,
  [LessonPlanStatus.CONTENT_GENERATED]: 6,
  [LessonPlanStatus.COMPLETED]: 6,
};

export type TechRatio = '1:1' | 'shared' | 'teacher' | 'none';

export interface TechDetails {
  chromebook: boolean;
  ipad: boolean;
  laptop: boolean;
  mobile: boolean;
  projector: boolean;
  smartboard: boolean;
  restrictedWifi: boolean;
}

export interface LessonInputData {
  topic: string;
  grade: number | null;
  tier1SubjectId: string | null;
  tier2SubjectId: string | null;
  usState: string | null;
  duration: string | null;
  instructionalModel: string | null;
  techRatio: TechRatio | null;
  techDetails: TechDetails;
}

// Shared types — re-exported from common metadata for backward compatibility
export type {
  Grade,
  Tier2Subject,
  Tier1Subject,
  Metadata as LessonPlanMetadata,
} from '../../../../common/services/metadata.models';

export interface LessonObjective {
  id: string;
  standard: string;
  /** Short, concise title for the objective (5-10 words) */
  title: string;
  /** Full, detailed learning objective statement */
  text: string;
  rationale: string;
  /** Original standard text from the official standards document (shortened by AI if too long) */
  standardText?: string;
  /** Specific criteria explaining why this standard aligns with the topic */
  alignmentCriteria: string[];
  selected: boolean;
  type: 'ai' | 'manual';
  /** Array of grade levels this standard applies to (e.g., [3, 4, 5]). 0 = Kindergarten. */
  grades: number[];
  /** Relevance score 0-10 (0 = not relevant, 10 = fully relevant) */
  relevance: number;
  /** Timestamp for sorting custom objectives */
  createdAt?: number;
}

export interface LessonBlock {
  id: number;
  time: string;
  duration: number;
  phase: string;
  activity: string;
  description: string;
}

/**
 * Slide types matching the Google Slides master template
 */
export type SlideType = 'greeting' | 'text' | 'list' | 'bye';

export interface GreetingSlideContent {
  title: string;
  subject: string;
  grade: string;
}

export interface TextSlideContent {
  title: string;
  subtitle: string;
}

export interface ListSlideContent {
  title: string;
  subtitle: string;
  items: string; // Items separated by \n
}

export interface ByeSlideContent {
  text: string;
}

export type SlideContent =
  | GreetingSlideContent
  | TextSlideContent
  | ListSlideContent
  | ByeSlideContent;

export interface LessonSlide {
  type: SlideType;
  content: SlideContent;
  /** The block ID this slide belongs to (for lesson map ordering) */
  blockId: number;
  /** Teacher's script/speaker notes for this slide */
  teacherScript?: string;
  /** Local path to the thumbnail image (after downloading from Google) */
  thumbnailPath?: string;
}

export interface GoogleSlidesData {
  /** Google Slides presentation ID */
  presentationId: string;
  /** Public URL to view/edit the presentation */
  presentationUrl: string;
  /** When the presentation was created */
  createdAt: string;
}

export interface SlideThumbnail {
  slideIndex: number;
  thumbnailUrl: string;
  blockId: number;
}

export interface SlideThumbnailsResponse {
  lessonPlanId: string;
  presentationId: string | null;
  presentationUrl: string | null;
  thumbnails: SlideThumbnail[];
}

export const THEME_COLOR = '#f56600';

export const DEFAULT_TECH_DETAILS: TechDetails = {
  chromebook: false,
  ipad: false,
  laptop: false,
  mobile: false,
  projector: false,
  smartboard: false,
  restrictedWifi: false,
};

export const DEFAULT_INPUT_DATA: LessonInputData = {
  topic: '',
  grade: null,
  tier1SubjectId: null,
  tier2SubjectId: null,
  usState: null,
  duration: null,
  instructionalModel: null,
  techRatio: null,
  techDetails: { ...DEFAULT_TECH_DETAILS },
};

// Shared US states — re-exported from common metadata
export type { UsState } from '../../../../common/services/metadata.models';
export { US_STATES } from '../../../../common/services/metadata.models';
